import { useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import type { CherryPickEdgeDTO } from '@clearflow/shared';
import { api } from '../lib/api.ts';

interface Props {
  cp: CherryPickEdgeDTO;
  sourceBranch: string | undefined;
  targetBranch: string | undefined;
  /** Bord droit de la sidebar (viewport coords) — la tooltip s'accroche juste à droite, pas au curseur. */
  anchorX: number;
  /** Bornes verticales (viewport coords) de la ligne cherry-pick survolée, pour centrer la tooltip dessus. */
  rowTop: number;
  rowBottom: number;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onDeleted: () => void;
  // Notifié après une sauvegarde réussie de la note — le parent (RepoViewPage.tsx) n'a que
  // l'instantané de `cp` pris au survol qui a ouvert cette tooltip ; sans ça, fermer/rouvrir la
  // tooltip avant le prochain refetch du graphe réafficherait l'ancienne note.
  onNoteUpdated: (note: string | null) => void;
  // Survol d'un lien dans le graphe plutôt que de la liste "Cherry-Picks" de la sidebar : lecture
  // seule — pas d'édition de note ni de suppression depuis ce point d'entrée, ces actions restent
  // réservées à la sidebar (confirmation de suppression, feedback d'enregistrement).
  readOnly?: boolean;
}

const SIDEBAR_GAP = 5;
const VIEWPORT_MARGIN = 8;
// Même géométrie de pointe que MrTooltip (voir son commentaire) — rayon 6px + moitié hauteur
// pointe (8px), pour qu'elle ne chevauche jamais un coin arrondi.
const ARROW_MARGIN = 14;

// Positionnement/redimensionnement identiques à MrTooltip.tsx (même ancrage sur le bord de la
// sidebar, même pointe façon VS Code, même ResizeObserver pour suivre le contenu qui grandit une
// fois la confirmation de suppression affichée) — dupliqué plutôt que factorisé : les deux
// tooltips n'ont en commun que cette mécanique de positionnement, pas le contenu ni les données
// qu'elles affichent, et une abstraction commune ajouterait un niveau d'indirection pour deux
// seuls appelants.
export function CherryPickTooltip({
  cp,
  sourceBranch,
  targetBranch,
  anchorX,
  rowTop,
  rowBottom,
  onMouseEnter,
  onMouseLeave,
  onDeleted,
  onNoteUpdated,
  readOnly = false,
}: Props) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Édition de la note en place, validée par un bouton "Enregistrer" explicite (pas au blur —
  // trop implicite : quitter le champ ne devrait pas, à soi seul, déclencher une écriture).
  // lastSavedRef (pas juste cp.note, qui ne bouge plus une fois la tooltip ouverte — voir le
  // commentaire sur onNoteUpdated) suit la dernière valeur confirmée par le serveur, pour savoir
  // si le bouton a quelque chose à faire (noteDirty ci-dessous) et ne jamais renvoyer un PATCH
  // identique à ce qui est déjà enregistré.
  const [note, setNote] = useState(cp.note ?? '');
  const [savingNote, setSavingNote] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const lastSavedRef = useRef(cp.note ?? '');
  const noteDirty = note.trim() !== lastSavedRef.current;

  const handleSaveNote = async () => {
    const trimmed = note.trim();
    if (trimmed === lastSavedRef.current) return;
    setSavingNote(true);
    setNoteError(null);
    try {
      const updated = await api.updateCherryPickNote(cp.id, trimmed || null);
      lastSavedRef.current = updated.note ?? '';
      setNote(updated.note ?? '');
      onNoteUpdated(updated.note);
    } catch (e) {
      setNoteError((e as Error).message);
    } finally {
      setSavingNote(false);
    }
  };

  const handleNoteKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Escape') return;
    setNote(lastSavedRef.current);
  };

  const tooltipRef = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState(() => Math.max(VIEWPORT_MARGIN, rowTop));
  const [arrowTop, setArrowTop] = useState(() => (rowTop + rowBottom) / 2 - Math.max(VIEWPORT_MARGIN, rowTop));

  useLayoutEffect(() => {
    const el = tooltipRef.current;
    if (!el) return undefined;
    const reposition = () => {
      const height = el.offsetHeight;
      const rowCenterY = (rowTop + rowBottom) / 2;
      const ideal = rowCenterY - height / 2;
      const clampedTop = Math.max(
        VIEWPORT_MARGIN,
        Math.min(ideal, window.innerHeight - height - VIEWPORT_MARGIN),
      );
      setTop(clampedTop);
      setArrowTop(Math.max(ARROW_MARGIN, Math.min(rowCenterY - clampedTop, height - ARROW_MARGIN)));
    };
    reposition();
    const ro = new ResizeObserver(reposition);
    ro.observe(el);
    return () => ro.disconnect();
  }, [rowTop, rowBottom]);

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await api.deleteCherryPick(cp.id);
      onDeleted();
    } catch (e) {
      setError((e as Error).message);
      setDeleting(false);
    }
  };

  return (
    <div
      ref={tooltipRef}
      className="cp-tooltip"
      style={{ left: anchorX + SIDEBAR_GAP, top, '--arrow-top': `${arrowTop}px` } as React.CSSProperties}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="cp-tooltip-title">
        <span>Cherry-pick</span>
        <span className="muted">ajouté par {cp.createdByName}</span>
        <span className="muted">{new Date(cp.createdAt).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })}</span>
      </div>
      <div className="cp-tooltip-header">
        <code className="cp-tooltip-sha">{cp.sourceSha.slice(0, 8)}</code>
        <span className="muted">→</span>
        <code className="cp-tooltip-sha">{cp.targetSha.slice(0, 8)}</code>
      </div>
      <div className="cp-tooltip-branches muted">
        <span>{sourceBranch ?? '?'}</span>
        <span> → </span>
        <span>{targetBranch ?? '?'}</span>
      </div>
      {readOnly ? (
        cp.note && <div className="cp-tooltip-note cp-tooltip-note-readonly">{cp.note}</div>
      ) : (
        <div className="cp-tooltip-note">
          <textarea
            className="cp-tooltip-note-input"
            value={note}
            placeholder="Ajouter une note…"
            rows={2}
            maxLength={500}
            disabled={savingNote}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={handleNoteKeyDown}
          />
          <div className="cp-tooltip-note-actions">
            {noteError && <span className="badge error cp-tooltip-note-status">Erreur : {noteError}</span>}
            {(noteDirty || savingNote) && (
              <button
                type="button"
                className="cp-tooltip-note-save-btn"
                disabled={!noteDirty || savingNote}
                onClick={handleSaveNote}
              >
                {savingNote ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            )}
          </div>
        </div>
      )}
      {!readOnly && (
      <div className="cp-tooltip-actions">
        {error ? (
          <span className="badge error">Erreur : {error}</span>
        ) : confirming ? (
          <button
            className="cp-tooltip-delete-btn confirming"
            disabled={deleting}
            onClick={handleDelete}
          >
            {deleting ? 'Suppression…' : 'Confirmer la suppression ?'}
          </button>
        ) : (
          <button className="cp-tooltip-delete-btn" onClick={() => setConfirming(true)}>
            Supprimer
          </button>
        )}
      </div>
      )}
    </div>
  );
}
